import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { withValue } from '@g4/automation-hooks';
import tracking from '@g4/g4-analytics';

import { useTranslations } from '../../../../graphql/components/ApplicationQueryProvider';
import {
  Box,
  Text,
  VerticalSpacing,
  Button,
  Spinner,
  useMedia,
} from '../../../../components/Boneyard';
import Markdown from '../../../../components/Markdown';
import { peacockBlue } from '@g4/boneyard/lib/theme/colors';

const propTypes = {
  description: PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.string),
  theme: PropTypes.object,
  isSelected: PropTypes.bool,
  showSpinner: PropTypes.bool,
  automationHook: PropTypes.string,
  positionGA: PropTypes.number,
  ribbon: PropTypes.string,
  cardTitle: PropTypes.string,
  ctaLabel: PropTypes.string,
  desktopImage: PropTypes.string,
  mobileImage: PropTypes.string,
  target: PropTypes.string,
  url: PropTypes.string,
};

const Card = styled(Box)`
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: ${props =>
    props.theme.ancillariesColors.extrasCardBackground};
  border-radius: 8px;
  overflow: hidden;
  color: ${props => props.theme.ancillariesColors.extrasCardText};
  height: 350px;
  z-index: 0;
  ${props => props.theme.query.lg} {
    height: 100%;
  }
`;

const CardHeader = styled.div`
  background-size: cover;
  background-position: center;
  background-image: url(${({ backgroundImage }) => backgroundImage});
  height: 150px;
`;

const CardContents = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  padding: 0;
  ${props => props.theme.query.lg} {
    flex-direction: row;
    padding: ${props => props.theme.spacing(4)}
      ${props => props.theme.spacing(6)};
  }
`;

const CardBadge = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing(2)};
  left: ${props => props.theme.spacing(2)};
  display: flex;
  height: 30px;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  border-radius: 15px;
  padding: ${props => props.theme.spacing(2)};
  color: ${peacockBlue};
  background-color: ${props => props.backgroundColor};
`;

const CardText = styled.div`
  flex-grow: 1;
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(4)};
  ${props => props.theme.query.lg} {
    padding: 0;
  }
  text-align: ${props => (props.mq.lte('sm') ? 'center' : 'left')};

  [data-hook='highlighted-deals_title'] {
    min-height: 50px;
  }
`;

const CardPrice = styled.div`
  border-radius: 0 0 8px 8px;
  border: 1px solid
    ${props => props.theme.ancillariesColors.extrasCardBackground};

  ${props => props.theme.query.lg} {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    align-items: flex-end;
    padding: ${props => props.theme.spacing(15)} 0 0;
    margin-left: ${props => props.theme.spacing(2)};
    background-color: transparent;
    border: none;
    margin-top: 0;
  }
`;

const DescriptionParagraph = styled(Text)`
  &:not(:first-child) {
    margin-top: ${props => props.theme.spacing(1)};
  }
  ${props => props.theme.query.lg} {
    &:not(:first-child) {
      margin-top: ${props => props.theme.spacing(2)};
    }
  }
`;

const DealsCard = ({
  automationHook,
  showSpinner,
  description = '',
  features = [],
  ribbon,
  cardTitle,
  desktopImage,
  mobileImage,
  target,
  url,
  ctaLabel,
  positionGA,
}) => {
  const mq = useMedia();

  const impressionData = {
    creative: mq.gte('lg')
      ? desktopImage.split('/').slice(-1)[0]
      : mobileImage.split('/').slice(-1)[0],
    name: cardTitle.replace(/(<([^>]+)>)/gi, '').replace(/&nbsp;/g, ' '),
    position: `Highlighted Deals Slot ${positionGA + 1}-HomePage`,
  };

  // Refactor items when finalized with business for promos
  const itemsList = {
    ...impressionData,
    id: '',
    items: [
      {
        item_id: '',
        item_name: '',
        coupon: '',
        discount: '',
        affiliation: 'WWW - Home',
        index: '',
        item_brand: 'G4',
        item_category: '',
        item_category2: '',
        item_category3: '',
        item_category4: '',
        item_category5: '',
        price: '',
        quantity: '',
        item_promotion_name: cardTitle
          .replace(/(<([^>]+)>)/gi, '')
          .replace(/&nbsp;/g, ' '),
        item_promotion_id: '',
      },
    ],
  };
  const translations = useTranslations();

  const { highlightedDealsText } = translations.highlightedDeals;

  useEffect(() => {
    if (positionGA < 2) {
      tracking.trackPromoImpressionExternal(impressionData);
    } else {
      tracking.trackPromoView(itemsList);
    }
  }, [
    cardTitle,
    desktopImage,
    mobileImage,
    mq,
    positionGA,
    impressionData,
    itemsList,
  ]);

  const trackPromoClick = () => {
    if (positionGA < 2) {
      tracking.trackPromoClickExternal(impressionData);
    } else {
      tracking.trackPromoSelect(itemsList);
    }
  };

  return (
    <Card automationHook={withValue(automationHook, 'card')}>
      {showSpinner && (
        <Box
          position="absolute"
          backgroundColor="rgba(255, 255, 255, 0.8)"
          w="100%"
          h="100%"
          d="flex"
          zIndex={999}
          justifyContent="center"
        >
          <Spinner
            size="large"
            inline
            automationHook={withValue(automationHook, 'spinner')}
          />
        </Box>
      )}

      {ribbon && <CardBadge backgroundColor="white">{ribbon}</CardBadge>}

      <CardHeader
        tabIndex={0}
        role="img"
        aria-label={highlightedDealsText}
        backgroundImage={mq.gte('lg') ? desktopImage : mobileImage}
        alt={highlightedDealsText}
      />

      <CardContents>
        <CardText mq={mq}>
          <Text
            size={22}
            weight={500}
            height={298}
            automationHook={withValue(automationHook, 'title')}
          >
            <Markdown source={cardTitle} />
          </Text>

          <VerticalSpacing size={5} />

          {Boolean(description) && (
            <>
              {description.split('\n').map((descriptionPart, index) => (
                <DescriptionParagraph
                  size={11}
                  lh={1.33}
                  key={`description_part_${index}`}
                  mb={2}
                  automationHook={withValue(
                    automationHook,
                    `description-${index + 1}`
                  )}
                >
                  <Markdown source={descriptionPart} />
                </DescriptionParagraph>
              ))}

              {Boolean(features.length) && <VerticalSpacing size={3} />}
            </>
          )}
        </CardText>
        {target && url && (
          <CardPrice>
            <Button
              type="primary"
              target={target}
              as="a"
              href={url}
              automationHook={withValue(automationHook, 'button')}
              fullWidth
              onClick={() => trackPromoClick()}
            >
              {ctaLabel}
            </Button>
          </CardPrice>
        )}
      </CardContents>
    </Card>
  );
};

DealsCard.propTypes = propTypes;

export default withTheme(React.memo(DealsCard));
